'use client'

import React from 'react'
import type { SerializedEditorState } from 'lexical'
import { youtubeVideoId } from '@/lib/youtube-embed'

// Minimal Lexical-to-JSX serializer for rendering post content.
// Handles the features enabled in the Posts collection.

interface Node {
  type: string
  children?: Node[]
  text?: string
  format?: number
  tag?: string
  url?: string
  newTab?: boolean
  value?: { url?: string; alt?: string; caption?: string }
  fields?: Record<string, any>
  listType?: 'bullet' | 'number' | 'check'
  checked?: boolean
  indent?: number
}

const FORMAT_BOLD = 1
const FORMAT_ITALIC = 2
const FORMAT_UNDERLINE = 8
const FORMAT_STRIKETHROUGH = 4
const FORMAT_CODE = 16

const BARE_URL_RE = /\bhttps?:\/\/[^\s]+/gi

const LINK_CLASS =
  'text-[#13479c] underline underline-offset-2 decoration-[#13479c] hover:text-[#0f3a7a] hover:decoration-[#0f3a7a]'

function splitUrlAndTrailingPunct(url: string): { href: string; trail: string } {
  const m = url.match(/^(.*?)([.,);:]+)$/)
  if (m && m[1].length > 8) return { href: m[1], trail: m[2] }
  return { href: url, trail: '' }
}

function LinkAnchor({
  href,
  newTab,
  children,
}: {
  href: string
  newTab?: boolean
  children: React.ReactNode
}) {
  const external = /^https?:\/\//i.test(href)
  const tab = Boolean(newTab ?? external)
  return (
    <a
      href={href}
      className={LINK_CLASS}
      target={tab ? '_blank' : undefined}
      rel={tab ? 'noreferrer noopener' : undefined}
    >
      {children}
    </a>
  )
}

function serializeText(node: Node, autolink: boolean): React.ReactNode {
  const text = node.text ?? ''
  const wrap = (el: React.ReactNode) => {
    const fmt = node.format ?? 0
    if (fmt & FORMAT_CODE) el = <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono">{el}</code>
    if (fmt & FORMAT_BOLD) el = <strong>{el}</strong>
    if (fmt & FORMAT_ITALIC) el = <em>{el}</em>
    if (fmt & FORMAT_UNDERLINE) el = <u>{el}</u>
    if (fmt & FORMAT_STRIKETHROUGH) el = <s>{el}</s>
    return el
  }

  if (!autolink || !text) return wrap(text)

  BARE_URL_RE.lastIndex = 0
  if (!BARE_URL_RE.test(text)) return wrap(text)
  BARE_URL_RE.lastIndex = 0

  const parts: React.ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = BARE_URL_RE.exec(text)) !== null) {
    const { href, trail } = splitUrlAndTrailingPunct(match[0])
    if (match.index > last) parts.push(text.slice(last, match.index))
    parts.push(
      <LinkAnchor key={`u${key++}`} href={href}>
        {href}
      </LinkAnchor>,
    )
    if (trail) parts.push(trail)
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return wrap(parts)
}

function linkHref(node: Node): string {
  const fields = (node.fields ?? {}) as Record<string, any>
  if (typeof fields.url === 'string' && fields.url) return fields.url
  if (typeof node.url === 'string' && node.url) return node.url
  const doc = fields.doc
  const value = doc && typeof doc === 'object' ? (doc.value ?? doc) : null
  const slug = value && typeof value === 'object' ? value.slug : null
  const relation = typeof doc === 'object' && doc ? doc.relationTo : null
  if (slug && relation === 'posts') return `/resources/${slug}`
  if (slug && relation === 'services') return `/services/${slug}`
  if (typeof slug === 'string' && slug) return `/${slug}`
  return '#'
}

function serializeLink(node: Node): React.ReactNode {
  const href = linkHref(node)
  const fields = (node.fields ?? {}) as Record<string, any>
  const newTab = fields.newTab ?? node.newTab
  return (
    <LinkAnchor href={href} newTab={newTab}>
      {node.children ? serializeChildren(node.children, true) : href}
    </LinkAnchor>
  )
}

function serializeChildren(children: Node[], insideLink = false): React.ReactNode[] {
  return children.map((child, i) => (
    <React.Fragment key={i}>{serializeNode(child, insideLink)}</React.Fragment>
  ))
}

function serializeNode(node: Node, insideLink = false): React.ReactNode {
  switch (node.type) {
    case 'text':
      return serializeText(node, !insideLink)

    case 'paragraph':
      return <p>{node.children ? serializeChildren(node.children, insideLink) : null}</p>

    case 'heading': {
      const Tag = (node.tag ?? 'h2') as keyof JSX.IntrinsicElements
      return <Tag>{node.children ? serializeChildren(node.children, insideLink) : null}</Tag>
    }

    case 'list': {
      const items = node.children ? serializeChildren(node.children, insideLink) : null
      return node.listType === 'number' ? <ol>{items}</ol> : <ul>{items}</ul>
    }

    case 'listitem':
      return <li>{node.children ? serializeChildren(node.children, insideLink) : null}</li>

    case 'quote':
      return <blockquote>{node.children ? serializeChildren(node.children, insideLink) : null}</blockquote>

    case 'code':
      return (
        <pre>
          <code>{node.children ? serializeChildren(node.children, insideLink) : null}</code>
        </pre>
      )

    case 'link':
    case 'autolink':
      return serializeLink(node)

    case 'upload': {
      const val = node.value as any
      if (val?.url) {
        return (
          <figure className="my-6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={val.url} alt={val.alt || ''} className="rounded-lg w-full" />
            {val.caption && (
              <figcaption className="text-center text-sm text-muted-foreground mt-2">
                {val.caption}
              </figcaption>
            )}
          </figure>
        )
      }
      return null
    }

    case 'block': {
      const fields = node.fields ?? {}
      if (fields.blockType === 'youtube') {
        const id = youtubeVideoId(String(fields.url ?? ''))
        if (!id) return null
        return (
          <div className="relative my-8 aspect-video w-full overflow-hidden rounded-lg bg-black">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              title="YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        )
      }
      return null
    }

    case 'horizontalrule':
      return <hr />

    case 'linebreak':
      return <br />

    default:
      // Render unknown nodes with children if they have them
      if (node.children) {
        return <>{serializeChildren(node.children, insideLink)}</>
      }
      return null
  }
}

interface Props {
  data: SerializedEditorState | null | undefined
  className?: string
}

export default function LexicalContent({ data, className }: Props) {
  if (!data?.root?.children) return null

  return (
    <div className={className}>
      {(data.root.children as Node[]).map((node, i) => (
        <React.Fragment key={i}>{serializeNode(node)}</React.Fragment>
      ))}
    </div>
  )
}
